/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import React, { useEffect, useRef, useState } from "react";

import { Id } from "react-toastify";
import styled from "styled-components";

import { useCurrentUser } from "src/app/useCurrentUser";
import { Block } from "src/classes/block";
import { BasePlate } from "src/classes/plate/BasePlate/BasePlate";
import { Plate } from "src/classes/plate/Plate";
import { Toaster } from "src/classes/utilities/Toaster";
import { PlateTitle } from "src/components/Shared/ConnectMenu/PlateTitle";
import { PageScreenshot } from "src/components/Shared/PageThumbnail/PageScreenshot";
import { BorderControl } from "src/components/UI/Menus/BorderControl";
import { Menu } from "src/components/UI/Menus/Menu";
import { MenuItem } from "src/components/UI/Menus/MenuItem/MenuItem";
import { DEFAULT_THUMBNAIL_WIDTH } from "src/lib/constants";

interface Props {
  block: Block | null;
  plate: Plate | BasePlate | null;
  blockMouseEvents: boolean;
  closeMenu?: () => void;
}

/**
 * A cleaned up version of ConnectMenu. Only for Plates for now.
 */
export function ConnectMenu({ closeMenu = () => {}, block, plate, blockMouseEvents }: Props) {
  const ref = useRef<HTMLInputElement>(null);

  const user = useCurrentUser()!;

  const [query, setQuery] = useState<string>("");

  const ownPlates = user.observe((state) => state.plates);
  const sharedPlates = user.observe((state) => state.sharedPlates);
  const forbiddenConnectionIds = block ? block.forbiddenConnectionIds : plate ? [plate.snapshot.id] : [];
  const plates = [...ownPlates, ...sharedPlates].filter((p) => !forbiddenConnectionIds.includes(p.snapshot.id));

  const queryIsPlateTitle = plates.some((p) => p.snapshot.title === query);

  const handleConnect = async (passedPlate: BasePlate | null): Promise<Id | void> => {
    if (queryIsPlateTitle && !passedPlate) return Toaster.error("You already have a plate with that title");
    if (!passedPlate && !query) return Toaster.error("Please enter a title or select a plate");

    // create plate if necessary
    let toPlate = passedPlate;
    if (!toPlate) {
      toPlate = (await Plate.create(query)) || null;
      if (!toPlate) return;
    }

    if (block) {
      block.connect(toPlate);
    } else if (plate) {
      plate.connect(toPlate);
    }
    closeMenu();
  };

  useEffect(() => {
    if (ref.current) {
      // this stops a weird behavior in safari where the input field scrolls to the top of the page
      ref.current.focus({ preventScroll: true });
    }
  }, []);

  return (
    <Menu close={closeMenu} blockMouseEvents={blockMouseEvents} style={{ maxHeight: "365px", overflowY: "auto" }}>
      <SearchWrapper onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        <StyledMenuSearch placeholder="Search or create..." onChange={(e) => setQuery(e.target.value)} ref={ref} />
      </SearchWrapper>
      {plates.map(
        (toPlate) =>
          (!query || toPlate.snapshot.title.toLowerCase().includes(query.toLowerCase())) && (
            <MenuItem
              key={toPlate.snapshot.id}
              handleClick={() => handleConnect(toPlate)}
              hoverComponent={
                <BorderControl>
                  <PageScreenshot plate={toPlate} styles={{ width: `${DEFAULT_THUMBNAIL_WIDTH}px` }} />
                </BorderControl>
              }
            >
              <PlateTitle plate={toPlate} />
            </MenuItem>
          )
      )}
      {query && (
        <MenuItem
          handleClick={() => handleConnect(null)}
          style={{ color: queryIsPlateTitle ? "gray" : "#006f6f" }}
        >
          {`Create "${query}"`}
        </MenuItem>
      )}
    </Menu>
  );
}

const SearchWrapper = styled.div`
  position: "sticky";
  top: 0;
  left: 0;
  padding: 7px 7px;
  width: 100%;
  background-color: ${({ theme }) => theme.background};
`;

const StyledMenuSearch = styled.input`
  color: ${({ theme }) => theme.text.default};
  background-color: ${({ theme }) => theme.background};
  padding: 7px 10px;
  outline: none;
  border: ${({ theme }) => `1px solid ${theme.subtleContrast}}`};
  width: 100%;
  transition: 0.2s all;
  :hover {
    background: ${({ theme }) => theme.subtleContrast};
  }
  :focus {
    background: ${({ theme }) => theme.subtleContrast};
  }
`;
